'use client';

import { useState, useSyncExternalStore } from 'react';
import type { AgentChatState } from './agentChatController';
import { useAgentChat } from './AgentChatProvider';
import { ahpStreamDiagnostics } from './voiceDiagnostics';

export function useAhpStreamDiagnostics() {
    return useSyncExternalStore(ahpStreamDiagnostics.subscribe, ahpStreamDiagnostics.getSnapshot, ahpStreamDiagnostics.getSnapshot);
}

export function ahpDiagnosticsText(state: AgentChatState): string {
    const data = state.view?.diagnostics;
    const connection = state.reconnecting ? 'reconnecting' : state.loading ? 'loading'
        : state.error ? 'error' : state.view ? 'subscribed' : 'not connected';
    const header = `Connection: ${connection}\nEnvironment: ${state.development?.id ?? 'integration'}\nBranch: ${state.development?.branch ?? 'not reported'}`;
    if (!data) return `${header}\nNo AHP diagnostic snapshot yet.`;
    const turn = data.latestTurn;
    return `${header}\nProvider: ${data.provider}\nSession: ${data.sessionTitle}\nSession channel: ${data.sessionChannel}\nChat channel: ${data.chatChannel}\nWorkspace: ${data.workspace.join('\n')}\nMode: ${state.view?.interactionMode ?? 'unknown'}\nApproval: ${data.approvalMode}\nHost updated: ${data.observedAt}\nTurns: ${data.turnCount}\nActive turn: ${state.view?.activeTurnId ?? 'none'}\nLatest turn: ${turn?.id ?? 'none'}\nState: ${turn?.state ?? 'none'}\nStarted: ${turn?.startedAt ?? 'not reported'}\nTool calls: ${turn?.toolCalls ?? 0}\nPlan mode is not a filesystem sandbox.`;
}

export function AhpDiagnostics() {
    const { state } = useAgentChat();
    const stream = useAhpStreamDiagnostics();
    const [copyStatus, setCopyStatus] = useState('');
    return <details className="border rounded p-2">
        <summary>AHP diagnostics</summary>
        <pre className="whitespace-pre-wrap break-all text-xs mt-2">{`Stream: ${stream.phase}\nLast stream event: ${stream.updatedAt || 'none'}\n${ahpDiagnosticsText(state)}`}</pre>
        <button type="button" onClick={async () => {
            try {
                await navigator.clipboard.writeText(`Stream: ${stream.phase}\nLast stream event: ${stream.updatedAt}\n${ahpDiagnosticsText(state)}`);
                setCopyStatus('Copied.');
            } catch {
                console.error('[ahp-diagnostics] Clipboard copy failed.');
                setCopyStatus('Copy failed; select the text instead.');
            }
        }}>Copy diagnostic snapshot</button>
        <p role="status">{copyStatus}</p>
    </details>;
}
